/* eslint-disable no-nested-ternary */
import { ArrowDownward, ArrowUpward, Delete } from '@mui/icons-material';
import {
  Box,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { Dish, DishComponent, Ingredient, SemiProduct } from '../../apiTypes';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import currency from '../../utils/currency';
import { getComponentCost } from '../../utils/getPriceByUnit';
import getQntLabel from '../../utils/getQntLabel';
import { getInitMap } from '../../utils/map';
import AddIngredientButton from '../AddIngredientButton';
import Image from '../Image';
import FormQntInput from './FormQntInput';
import FormSelect from './FormSelect';

export type FormComponentsTableProps = {
  options: (Ingredient & { maxQnt?: number })[];
  form: UseFormReturn<Dish | SemiProduct>;
  name?: unknown;
  shouldSelectStore?: boolean;
  shouldShowCosts?: boolean;
  handleAddStore?: () => void;
  small?: boolean;
  title?: string;
  disablePackingInit?: boolean;
  price?: number;
  requiredComponents?: DishComponent[];
  canBeNegative?: boolean;
};

const FormComponentsTable = ({
  options,
  name = 'components',
  form,
  shouldSelectStore,
  shouldShowCosts,
  small = false,
  handleAddStore,
  title,
  disablePackingInit = false,
  price,
  requiredComponents = [],
  canBeNegative,
}: FormComponentsTableProps) => {
  const { lang } = useLocale();
  const { stores, storesMap, productsMap, semiProductsMap } = useCafe();
  const { cafe } = useAdmin();

  const [disablePacking, setDisablePacking] = useState(disablePackingInit);

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: name as 'components',
    keyName: 'key',
  });

  const filteredOptions = options.filter((o) => !fields.some((c) => c.id === o.id));

  const optionsMap = useMemo(
    () => options.reduce((acc, cur) => ({ ...acc, [cur.id]: cur }), getInitMap<Ingredient & { maxQnt?: number }>()),
    [options]
  );

  const components = form.watch('components');

  const costs = components.map((c) => ({
    cost: getComponentCost(c, productsMap, semiProductsMap),
    prevCost: getComponentCost(c, productsMap, semiProductsMap, true),
  }));

  const requiredCosts = useMemo(
    () =>
      requiredComponents.map((c) => ({
        cost: getComponentCost(c, productsMap, semiProductsMap),
        prevCost: getComponentCost(c, productsMap, semiProductsMap, true),
      })),
    [productsMap, requiredComponents, semiProductsMap]
  );

  const totalRequiredCost = useMemo(() => requiredCosts.reduce((acc, c) => acc + c.cost, 0), [requiredCosts]);
  const totalPrevRequiredCost = useMemo(() => requiredCosts.reduce((acc, c) => acc + c.prevCost, 0), [requiredCosts]);

  const totalCost = costs.reduce((acc, c) => acc + c.cost, 0) + totalRequiredCost;
  const totalPrevCost = costs.reduce((acc, c) => acc + c.prevCost, 0) + totalPrevRequiredCost;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 1 }}>
        {fields.length > 0 && (
          <Typography variant="body2" color={(t) => t.palette.text.secondary}>
            {title}
          </Typography>
        )}

        {fields.length > 0 && (
          <FormControlLabel
            control={
              <Switch size="small" checked={!disablePacking} onChange={(e) => setDisablePacking(!e.target.checked)} />
            }
            label={t.display_packaging[lang]}
          />
        )}
      </Box>
      <TableContainer>
        <Table sx={{ width: '100%' }}>
          <TableHead>
            <TableRow
              sx={{
                th: {
                  border: 'none',
                },
              }}
            >
              <TableCell size="small">{t.component[lang]}</TableCell>
              {shouldSelectStore && <TableCell size="small">{t.store[lang]}</TableCell>}
              <TableCell size="small">{t.quantity[lang]}</TableCell>
              {shouldShowCosts && <TableCell size="small">{t.cost[lang]}</TableCell>}
              <TableCell size="small" />
            </TableRow>
          </TableHead>

          <TableBody>
            {requiredComponents.map((component, index) => {
              const cost = requiredCosts[index]?.cost || 0;

              return (
                <TableRow
                  key={component.id}
                  sx={{
                    ':hover': {
                      bgcolor: (t) => `${t.palette.primary.light}11`,
                    },
                    td: {
                      border: 'none',
                    },
                  }}
                >
                  <TableCell size="small">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Image src={optionsMap[component.id]?.imageUrl} />
                      <Box>
                        <Typography sx={{ lineHeight: 0.8 }}>{optionsMap[component.id]?.name}</Typography>

                        {optionsMap[component.id]?.maxQnt !== undefined && (
                          <Typography
                            variant="caption"
                            sx={{ lineHeight: 0.8 }}
                            color={(t) => t.palette.text.secondary}
                          >{`${t.available[lang]}: ${optionsMap[component.id]?.maxQnt} ${
                            t[optionsMap[component.id]?.unit || 'g'][lang]
                          }`}</Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>

                  {shouldSelectStore && (
                    <TableCell size="small">
                      <Typography>{storesMap[component.store || '']?.name}</Typography>
                    </TableCell>
                  )}

                  <TableCell size="small">
                    <Typography>
                      {getQntLabel(component.qnt, optionsMap[component.id], lang, disablePacking)}
                    </Typography>
                  </TableCell>

                  {shouldShowCosts && (
                    <TableCell size="small">
                      <Typography>{currency(cost, cafe?.currency, true)}</Typography>
                    </TableCell>
                  )}

                  <TableCell size="small" />
                </TableRow>
              );
            })}

            {fields.map((field, index) => {
              const cost = costs[index]?.cost || 0;
              const prevCost = costs[index]?.prevCost || 0;
              return (
                <TableRow
                  key={field.key}
                  sx={{
                    ':hover': {
                      bgcolor: (t) => `${t.palette.primary.light}11`,
                    },
                    td: {
                      border: 'none',
                    },
                  }}
                  data-testid={`components.${index}`}
                >
                  <TableCell size="small">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Image src={optionsMap[field.id]?.imageUrl} />
                      <Box>
                        <Typography sx={{ lineHeight: 0.8 }}>{optionsMap[field.id]?.name}</Typography>

                        {optionsMap[field.id]?.maxQnt !== undefined && (
                          <Typography
                            variant="caption"
                            sx={{ lineHeight: 0.8 }}
                            color={(t) => t.palette.text.secondary}
                          >{`${t.available[lang]}: ${optionsMap[field.id]?.maxQnt} ${
                            t[optionsMap[field.id]?.unit || 'g'][lang]
                          }`}</Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>

                  {shouldSelectStore && (
                    <TableCell size="small">
                      <FormSelect
                        name={`${name}.${index}.store`}
                        size="small"
                        options={stores.map((w) => w.id)}
                        renderOption={(o) => storesMap[o]?.name}
                        onAddNew={handleAddStore}
                        addButtonText={t.add_store[lang]}
                      />
                    </TableCell>
                  )}

                  <TableCell size="small">
                    <FormQntInput
                      ingredient={{ unit: optionsMap[field.id]?.unit || 'g', qpp: optionsMap[field.id]?.qpp }}
                      name={`${name}.${index}.qnt`}
                      disablePacking={disablePacking}
                      canBeNegative={canBeNegative}
                    />
                  </TableCell>

                  {shouldShowCosts && (
                    <TableCell size="small">
                      <Typography>{currency(cost, cafe?.currency, true)}</Typography>
                      {cost !== prevCost && (
                        <Typography
                          variant="body2"
                          sx={{
                            lineHeight: 0.8,
                            color: (t) => (cost < prevCost ? t.palette.success.dark : t.palette.error.dark),
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          {cost < prevCost ? (
                            <ArrowDownward sx={{ fontSize: 14 }} />
                          ) : (
                            <ArrowUpward sx={{ fontSize: 14 }} />
                          )}
                          {`${cost < prevCost ? '' : '+'}${currency(cost - prevCost)}`}
                        </Typography>
                      )}
                    </TableCell>
                  )}

                  <TableCell size="small">
                    <IconButton size="small" onClick={() => remove(index)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ maxWidth: 260, my: small ? 0 : 0.5, ml: 1 }}>
        <AddIngredientButton
          options={filteredOptions}
          small={small}
          onAdd={(value) =>
            append({
              type: value.type,
              id: value.id,
              store: stores[0]?.id || '',
              qnt: 0,
            })
          }
        />
      </Box>

      {shouldShowCosts && (
        <>
          {!!price && (
            <Typography variant="h6" textAlign="right">
              {t.price[lang]}: {currency(price, cafe?.currency)}
            </Typography>
          )}

          <Typography variant="h6" textAlign="right">
            {t.cost_price[lang]}: {currency(totalCost, cafe?.currency)}
            <Typography component="span" sx={{ color: (t) => t.palette.text.secondary }}>
              {price ? ` (${Math.round((totalCost / price) * 100)}% ${t.of_price[lang]})` : ''}
            </Typography>
          </Typography>

          {totalCost !== totalPrevCost && (
            <Typography
              variant="body2"
              sx={{
                lineHeight: 0.8,
                color: (t) => (totalCost < totalPrevCost ? t.palette.success.dark : t.palette.error.dark),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0.5,
              }}
            >
              {totalCost < totalPrevCost ? (
                <ArrowDownward sx={{ fontSize: 14 }} />
              ) : (
                <ArrowUpward sx={{ fontSize: 14 }} />
              )}
              {`${totalCost < totalPrevCost ? '' : '+'}${currency(totalCost - totalPrevCost, cafe?.currency)}`}
              {price
                ? ` (${totalCost < totalPrevCost ? '' : '+'}${Math.round(
                    ((totalCost - totalPrevCost) / price) * 100
                  )}% ${t.of_price[lang]})`
                : ''}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default FormComponentsTable;
